<template>
  <div ref="viewport" :class="style.mainContent">
    <div
      v-if="isDesktopVersion"
      :class="style.previewSizeBox"
      :style="previewSizeBoxStyle"
    >
      <div :class="style.previewScaleLayer" :style="previewScaleLayerStyle">
        <div :class="previewClasses">
          <LoaderComponent v-if="isLoading" />

          <main
            v-else
            id="main"
            ref="main"
            :class="style.main"
            :style="mainStyle"
            @scroll="$emit('main-scroll', $event)"
          >
            <slot />
          </main>
        </div>
      </div>
    </div>

    <div v-else :class="previewClasses">
      <LoaderComponent v-if="isLoading" />

      <main
        v-else
        id="main"
        ref="main"
        :class="style.main"
        :style="mainStyle"
        @scroll="$emit('main-scroll', $event)"
      >
        <slot />
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, nextTick, PropType} from "vue";

import style from "./LandingPreviewFrame.module.scss";
import {VersionType} from "@/page/quiz/typeQuiz";
import LoaderComponent from "@/elements/UI/overlay/Loader/LoaderComponent.vue";

type ScaleStyle = {
  width: string;
  height: string;
  transform?: string;
};

export default defineComponent({
  name: "LandingPreviewFrame",

  components: {
    LoaderComponent
  },

  props: {
    version: {
      type: String as PropType<VersionType>,
      required: true
    },

    isLoading: {
      type: Boolean,
      required: true
    },

    mainStyle: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    }
  },

  emits: ["main-scroll"],

  data() {
    return {
      style,
      baseDesktopWidth: 1600,
      baseDesktopHeight: 900,
      viewportWidth: 0,
      viewportHeight: 0,
      resizeObserver: null as ResizeObserver | null
    };
  },

  mounted() {
    this.observeViewport();
  },

  beforeUnmount() {
    this.unobserveViewport();
  },

  methods: {
    observeViewport(): void {
      nextTick(() => {
        const viewport = this.$refs.viewport as HTMLElement | undefined;

        if (!viewport) {
          return;
        }

        this.updateViewportSize();

        this.resizeObserver = new ResizeObserver(() => {
          this.updateViewportSize();
        });

        this.resizeObserver.observe(viewport);
      });
    },

    unobserveViewport(): void {
      if (!this.resizeObserver) {
        return;
      }

      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    },

    updateViewportSize(): void {
      const viewport = this.$refs.viewport as HTMLElement | undefined;

      if (!viewport) {
        return;
      }

      const coordinates = viewport.getBoundingClientRect();
      const viewportStyles = window.getComputedStyle(viewport);

      const paddingLeft = parseFloat(viewportStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(viewportStyles.paddingRight) || 0;
      const paddingTop = parseFloat(viewportStyles.paddingTop) || 0;
      const paddingBottom = parseFloat(viewportStyles.paddingBottom) || 0;

      this.viewportWidth = Math.max(
        coordinates.width - paddingLeft - paddingRight,
        0
      );

      this.viewportHeight = Math.max(
        coordinates.height - paddingTop - paddingBottom,
        0
      );
    }
  },

  computed: {
    isMobileVersion(): boolean {
      return this.version === "Мобильная";
    },

    isDesktopVersion(): boolean {
      return !this.isMobileVersion;
    },

    desktopScale(): number {
      if (!this.viewportWidth || !this.viewportHeight) {
        return 1;
      }

      const scaleX = this.viewportWidth / this.baseDesktopWidth;
      const scaleY = this.viewportHeight / this.baseDesktopHeight;

      return Math.min(scaleX, scaleY, 1);
    },

    previewSizeBoxStyle(): ScaleStyle {
      return {
        width: `${this.baseDesktopWidth * this.desktopScale}px`,
        height: `${this.baseDesktopHeight * this.desktopScale}px`
      };
    },

    previewScaleLayerStyle(): ScaleStyle {
      return {
        width: `${this.baseDesktopWidth}px`,
        height: `${this.baseDesktopHeight}px`,
        transform: `scale(${this.desktopScale})`
      };
    },

    previewClasses(): string[] {
      return [
        this.style.landingPage,
        this.isMobileVersion ? this.style.mobile : this.style.desktop
      ];
    }
  },

  watch: {
    version(): void {
      nextTick(() => {
        this.updateViewportSize();
      });
    }
  }
});
</script>
